import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PizzaCard from '../components/PizzaCard';
import { Button } from '../components/ui/button';
import { addToCart } from '../data/mock';
import { toast } from 'sonner';
import { 
  traditionnelles, 
  specialitesTomate, 
  specialitesCreme, 
  salades, 
  desserts, 
  boissons,
  allPizzas 
} from '../data/mock';
import { Filter } from 'lucide-react';

const Menu = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [items, setItems] = useState([]);

  const specialites = allPizzas.filter(p => p.name === "La CASA" || p.name === "El Professor");

  useEffect(() => {
    const category = searchParams.get('category') || 'all';
    setSelectedCategory(category);
    loadItems(category);
  }, [searchParams]);

  const loadItems = (category) => {
    switch (category) {
      case 'traditionnelles':
        setItems(traditionnelles);
        break;
      case 'tomate':
        setItems(specialitesTomate);
        break;
      case 'creme':
        setItems(specialitesCreme);
        break;
      case 'specialites':
        setItems(specialites);
        break;
      case 'salades':
        setItems(salades);
        break;
      case 'desserts':
        setItems(desserts);
        break;
      case 'boissons':
        setItems(boissons);
        break;
      default:
        setItems(allPizzas);
    }
  };

  const categories = [
    { id: 'all', name: 'Toutes les Pizzas' },
    { id: 'traditionnelles', name: 'Traditionnelles' },
    { id: 'tomate', name: 'Base Tomate' },
    { id: 'creme', name: 'Base Crème' },
    { id: 'specialites', name: 'Nos Spécialités' },
    { id: 'salades', name: 'Salades' },
    { id: 'desserts', name: 'Desserts' },
    { id: 'boissons', name: 'Boissons' }
  ];

  const isPizzaCategory = ['all', 'traditionnelles', 'tomate', 'creme', 'specialites'].includes(selectedCategory);

  const handleCategoryChange = (categoryId) => {
    setSearchParams({ category: categoryId });
  };

  return (
    <div className="bg-black min-h-screen pt-32 pb-16">
      {/* Hero Banner */}
      <div className="relative py-20 mb-12 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1579751626657-72bc17010498?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHx3b29kJTIwZmlyZWQlMjBwaXp6YXxlbnwwfHx8fDE3NzA2NzI0NTd8MA&ixlib=rb-4.1.0&q=85"
            alt="Menu"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/70"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-transparent to-transparent"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Notre <span className="text-red-600">Menu</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl">
            Découvrez toutes nos pizzas au feu de bois, salades et desserts
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* Category Filter */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Filter className="text-red-500" size={24} />
            <h2 className="text-2xl font-bold text-white">Filtrer par catégorie</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                data-testid={`filter-${category.id}`}
                className={`
                  ${selectedCategory === category.id 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-white hover:border-red-600/50'
                  } 
                  transition-all duration-300 font-semibold
                `}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Items Grid */}
        <div className="mb-8">
          <h3 className="text-3xl font-bold text-white mb-6">
            {categories.find(c => c.id === selectedCategory)?.name}
            <span className="text-gray-500 text-xl ml-3">({items.length} {items.length > 1 ? 'produits' : 'produit'})</span>
          </h3>
        </div>

        {items.length > 0 ? (
          <>
            {/* For pizzas - use PizzaCard */}
            {isPizzaCategory && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {items.map((item) => (
                  <PizzaCard key={item.id} pizza={item} />
                ))}
              </div>
            )}

            {/* For salades, desserts, boissons - simpler card layout */}
            {!isPizzaCategory && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {items.map((item) => (
                  <div 
                    key={item.id}
                    className="bg-gradient-to-br from-gray-900 to-black rounded-xl overflow-hidden border border-gray-800 hover:border-red-600/50 transition-all duration-300"
                  >
                    {item.image && (
                      <div className="h-48 overflow-hidden">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-2">{item.name}</h3>
                      {item.ingredients && (
                        <p className="text-gray-400 text-sm mb-4">{item.ingredients}</p>
                      )}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                        <div className="text-2xl font-bold text-red-500">
                          {item.price.toFixed(2)}€
                        </div>
                        <Button 
                          size="sm"
                          onClick={() => {
                            addToCart(item);
                            toast.success(`${item.name} ajouté au panier !`);
                            window.dispatchEvent(new Event('storage'));
                          }}
                          className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold"
                          data-testid={`add-simple-${item.id}`}
                        >
                          Ajouter
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-500 text-xl">Aucun produit dans cette catégorie</p>
          </div>
        )}

        {/* Note about Calzone - Only for pizza categories */}
        {isPizzaCategory && (
          <div className="mt-12 bg-red-600/20 border border-red-500/50 rounded-xl p-6 text-center">
            <p className="text-red-400 font-semibold text-lg">
              Toutes nos pizzas sont réalisables en chaussons (Calzone) !
            </p>
            <p className="text-gray-400 mt-2">
              Cochez la case "En chausson" sur la pizza de votre choix
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Menu;
